import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Typography,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Autocomplete,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';

// Country codes and validation schema
import { COUNTRY_OPTIONS } from '../utils/countryDialCodes';
const COUNTRY_CODES = Array.from(new Set(COUNTRY_OPTIONS.map(c => c.dialCode)));
const validationSchema = yup.object({
  candidateName: yup.string().required('Candidate name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  countryCode: yup.string().oneOf(COUNTRY_CODES).required('Country code is required'),
  mobileNumberLocal: yup
    .string()
    .matches(/^\d{1,15}$/, 'Enter up to 15 digits')
    .required('Mobile number is required'),
  department: yup.string().required('Department is required'),
  designation: yup.string().required('Designation is required'),
  employeeGrade: yup.string().required('Employee grade is required'),
  dateOfJoining: yup.date().required('Date of joining is required'),
  placeOfJoining: yup.string().required('Place of joining is required'),
});

const ITOnboardingFormDetails = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('mode') === 'edit';
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await fetch(`${API_URL}/departments/public`);
        const data = await res.json();
        setDepartments(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to load departments', e);
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, []);

  // Formik setup
  const formik = useFormik({
    initialValues: {
      candidateName: '',
      email: '',
      countryCode: '+91',
      mobileNumberLocal: '',
      mobileNumber: '',
      department: '',
      designation: '',
      employeeGrade: '',
      dateOfJoining: '',
      placeOfJoining: '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const localDigits = (values.mobileNumberLocal || '').replace(/\D/g, '');
        const cc = values.countryCode || '+91';
        const combinedDigits = `${cc.replace('+','')}${localDigits}`;
        if (combinedDigits.length > 15) {
          throw new Error('Mobile number must be at most 15 digits including country code');
        }
        const payload = {
          ...values,
          mobileNumber: `+${combinedDigits}`,
        };
        delete payload.mobileNumberLocal;
        delete payload.countryCode;
        const response = await fetch(`${API_URL}/it-onboarding/it-form/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (response.ok) {
          toast.success('Form updated successfully!');
          setFormData(data.data);
          // Update URL to remove edit mode
          window.history.replaceState({}, '', window.location.pathname);
        } else {
          toast.error(data.message || 'Update failed');
        }
      } catch (err) {
        console.error(err);
        toast.error('An error occurred while updating the form.');
      }
    },
  });

  const fetchForm = useCallback(async () => {
    try {
      console.log('🔍 Fetching IT form for ID:', id);
      const res = await fetch(`${API_URL}/it-onboarding/it-form/${id}`);
      console.log('📡 Response status:', res.status);
      
      const data = await res.json();
      console.log('📄 Response data:', data);
      
      if (res.ok) {
        setFormData(data.data);
        // Populate formik with fetched data
        formik.setValues({
          candidateName: data.data.candidateName || '',
          email: data.data.email || '',
          // Try to parse country code and local part from stored mobileNumber like +CCNNN...
          countryCode: (() => {
            const v = data.data.mobileNumber || '';
            const match = v.match(/^\+(\d{1,3})/);
            const cc = match ? `+${match[1]}` : '+91';
            return COUNTRY_CODES.includes(cc) ? cc : '+91';
          })(),
          mobileNumberLocal: (() => {
            const v = data.data.mobileNumber || '';
            const m = v.replace(/^\+\d{1,3}/, '');
            return m;
          })(),
          mobileNumber: data.data.mobileNumber || '',
          department: data.data.department || '',
          designation: data.data.designation || '',
          employeeGrade: data.data.employeeGrade || '',
          dateOfJoining: data.data.dateOfJoining ? new Date(data.data.dateOfJoining).toISOString().split('T')[0] : '',
          placeOfJoining: data.data.placeOfJoining || '',
        });
      } else {
        console.error('❌ Failed to fetch IT form:', data);
        toast.error(data.message || 'Failed to load form data');
      }
    } catch (err) {
      console.error('💥 Error fetching IT form:', err);
      toast.error('An error occurred while loading the form.');
    } finally {
      setLoading(false);
    }
  }, [id, formik]);

  useEffect(() => {
    fetchForm();
  }, [fetchForm]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (!formData) {
    return (
      <Typography variant="h6" color="error" align="center" mt={4}>
        Form not found or an error occurred.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 700,
        mx: 'auto',
        mt: 6,
        px: { xs: 2, sm: 4 },
        py: 3,
      }}
    >
      <Typography
        align="center"
        sx={{ fontSize: '1.75rem', fontWeight: 600, mb: 3 }}
      >
        IT - Employee Onboarding Details
        {isEditMode && <Typography component="span" sx={{ ml: 1, color: 'primary.main' }}>(Edit Mode)</Typography>}
      </Typography>

      {isEditMode ? (
        // Edit Mode - Form
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={3}>
            <TextField
              label="Candidate Name"
              name="candidateName"
              value={formik.values.candidateName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.candidateName && Boolean(formik.errors.candidateName)}
              helperText={formik.touched.candidateName && formik.errors.candidateName}
              fullWidth
              required
            />

            <TextField
              label="Email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              fullWidth
              required
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Autocomplete
                sx={{ minWidth: 260 }}
                options={COUNTRY_OPTIONS}
                getOptionLabel={(opt) => opt?.dialCode || ''}
                filterOptions={(options, { inputValue }) => {
                  const q = (inputValue || '').toLowerCase();
                  return options.filter(o =>
                    (o.name || '').toLowerCase().includes(q)
                    || (o.iso2 || '').toLowerCase().includes(q)
                    || (o.iso3 || '').toLowerCase().includes(q)
                    || (o.dialCode || '').includes(inputValue)
                  );
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    {`${option.iso2 || option.iso3 || option.name} (${option.dialCode})`}
                  </li>
                )}
                value={COUNTRY_OPTIONS.find(o => o.dialCode === formik.values.countryCode) || null}
                onChange={(_, newVal) => {
                  formik.setFieldValue('countryCode', newVal?.dialCode || '+91');
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Country Code" placeholder="Type country or code" />
                )}
              />
              <TextField
                label="Mobile Number"
                name="mobileNumberLocal"
                value={formik.values.mobileNumberLocal}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.mobileNumberLocal && Boolean(formik.errors.mobileNumberLocal)}
                helperText={formik.touched.mobileNumberLocal && formik.errors.mobileNumberLocal}
                fullWidth
                required
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 15 }}
              />
            </Box>

            <FormControl fullWidth required error={formik.touched.department && Boolean(formik.errors.department)}>
              <InputLabel>Department</InputLabel>
              <Select
                label="Department"
                name="department"
                value={formik.values.department}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <MenuItem value=""><em>Select department</em></MenuItem>
                {departments.map((d) => (
                  <MenuItem key={d._id || d.id || d.departmentName} value={d.departmentName}>
                    {d.departmentName}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.department && formik.errors.department && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {formik.errors.department}
                </Typography>
              )}
            </FormControl>

            <TextField
              label="Designation"
              name="designation"
              value={formik.values.designation}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.designation && Boolean(formik.errors.designation)}
              helperText={formik.touched.designation && formik.errors.designation}
              fullWidth
              required
            />

            <FormControl fullWidth required error={formik.touched.employeeGrade && Boolean(formik.errors.employeeGrade)}>
              <InputLabel>Employee Grade</InputLabel>
              <Select
                label="Employee Grade"
                name="employeeGrade"
                value={formik.values.employeeGrade}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <MenuItem value=""><em>Select grade</em></MenuItem>
                {Array.from({ length: 10 }, (_, i) => `L${i + 1}`).map(grade => (
                  <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                ))}
              </Select>
              {formik.touched.employeeGrade && formik.errors.employeeGrade && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {formik.errors.employeeGrade}
                </Typography>
              )}
            </FormControl>

            <TextField
              label="Date of Joining"
              name="dateOfJoining"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formik.values.dateOfJoining}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.dateOfJoining && Boolean(formik.errors.dateOfJoining)}
              helperText={formik.touched.dateOfJoining && formik.errors.dateOfJoining}
              fullWidth
              required
            />

            <TextField
              label="Place of Joining"
              name="placeOfJoining"
              value={formik.values.placeOfJoining}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.placeOfJoining && Boolean(formik.errors.placeOfJoining)}
              helperText={formik.touched.placeOfJoining && formik.errors.placeOfJoining}
              fullWidth
              required
            />

            <Button 
              type="submit" 
              variant="contained" 
              fullWidth 
              sx={{
                backgroundColor: '#673ab7',
                '&:hover': { backgroundColor: '#5e35b1' },
                fontWeight: 'bold',
                py: 1.5,
                mt: 2
              }}
            >
              Update Form
            </Button>
          </Stack>
        </form>
      ) : (
        // View Mode - Read-only fields
        <Grid container spacing={3}>
          {[
            { label: 'Candidate Name', value: formData.candidateName },
            { label: 'Email', value: formData.email },
            { label: 'Mobile Number', value: formData.mobileNumber },
            { label: 'Department', value: formData.department },
            { label: 'Designation', value: formData.designation },
            { label: 'Employee Grade', value: formData.employeeGrade },
            {
              label: 'Date of Joining',
              value: new Date(formData.dateOfJoining).toLocaleDateString(),
            },
            { label: 'Place of Joining', value: formData.placeOfJoining },
          ].map((field, index) => (
            <Grid item xs={12} key={index}>
              <TextField
                fullWidth
                label={field.label}
                value={field.value}
                InputProps={{ readOnly: true }}
                variant="outlined"
                InputLabelProps={{
                  sx: { color: 'text.primary', fontWeight: 500 },
                }}
                sx={{
                  input: { color: 'text.primary', fontWeight: 400, fontSize: '1rem' },
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ITOnboardingFormDetails; 